/* 左边的品牌列表 */
import React, { useRef, useState, useEffect } from 'react'
import { sendTeaCommEvent } from 'lib/tea-analyze'
import classNames from 'classnames'
import { IInfoProps } from './interface'
import applyHttpsImage from 'lib/apply-https-img'

interface IProps {
  letters: string[]
  brands: IInfoProps[][]
  brandId: number
  selectBrandId?: (id: number) => void
  selectBrandName?: (name: string) => void
}

const hot_star = require('static/hot-star.svg')

export default function Brand({
  letters,
  brands,
  selectBrandId,
  selectBrandName,
  brandId = 0,
}: IProps) {
  // 获取需要监听的dom
  const divRef = useRef<HTMLDivElement>(null)
  // 获取需要滚动的字母dom
  const letterRef = useRef<HTMLUListElement>(null)
  // 当前所在的字母
  const [current, setCurrent] = useState('*')
  // 重新定位的位置集合
  const offsetMap = new Map()

  const letterMap = new Map()
  useEffect(() => {
    // 左侧字母始终呈现到可视区域
    try {
      if (
        divRef.current &&
        letterRef.current &&
        letterRef.current?.scrollHeight > letterRef.current?.clientHeight
      ) {
        if (letterMap.size === 0) {
          letters.forEach((letter: string, index: number) => {
            letterMap.set(letter, index)
          })
        }

        const scale = letterMap.get(current) / letters.length
        // 顶部预留大概一个字母的空间
        letterRef.current.scrollTop =
          letterRef.current?.scrollHeight * scale - 32
      }
    } catch (error) {
      console.error(error)
    }
  }, [current])

  useEffect(() => {
    if (!divRef.current) {
      return
    }
    const observer: IntersectionObserver = addObserver()
    divRef.current &&
      [...divRef.current.children].forEach((ele: Element) => {
        observer.observe(ele)
      })
    if (brandId) {
      // 滚动品牌列表到指定位置
      requestAnimationFrame(() => {
        if (divRef.current) {
          const target = divRef.current.querySelector(`.brand-${brandId}`)
          if (!target) {
            return
          }
          // 避免字母遮挡
          divRef.current.scrollTop = (target as HTMLElement).offsetTop - 32
        }
      })
    }
    return () => {
      observer.disconnect()
    }
    // eslint-disable-next-line
  }, [brandId])

  // 监听品牌位置
  function addObserver(): IntersectionObserver {
    return new IntersectionObserver(
      (changes) => {
        changes.forEach((change: any) => {
          const letter = change.target.getAttribute('data-letter') as string
          const index = letters.findIndex((ele) => ele === letter)
          if (
            change.rootBounds.top >= change.boundingClientRect.top &&
            change.rootBounds.top <= change.boundingClientRect.bottom &&
            change.isIntersecting
          ) {
            setCurrent(letter)
          } else if (
            change.boundingClientRect.bottom <= change.rootBounds.bottom &&
            !change.isIntersecting &&
            change.intersectionRatio <= 0
          ) {
            setCurrent(letters[index + 1])
          }
        })
      },
      {
        root: divRef.current as unknown as Element,
        rootMargin: '0px 0px 0px 0px',
        threshold: 0.0,
      }
    )
  }

  // 得到每个字母点击要定的位置
  function getOffsetTop() {
    divRef.current &&
      Array.from(divRef.current.children).forEach((item: Element) => {
        offsetMap.set(
          item.getAttribute('data-letter'),
          (item as HTMLElement).offsetTop
        )
      })
  }

  // 点击以后将右边滚动到对应的字母位置
  function scrollView(letter: string) {
    if (offsetMap.size === 0) {
      getOffsetTop()
    }
    if (offsetMap.get(letter) !== undefined) {
      if (divRef.current) {
        divRef.current.scrollTop = offsetMap.get(letter)
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setCurrent(letter)
        })
      })
    }
  }

  function renderLetters() {
    return (
      <ul className="fl g-hide-scroll-bar letter-wrap tw-w-22" ref={letterRef}>
        {letters.map((letter: string) => (
          <li
            key={letter}
            className={classNames('tw-w-18 tw-ml-2 tw-mr-2', {
              'tw-h-18 md:tw-h-22 xl:tw-h-24 tw-text-14': letter !== '*',
              'tw-text-0': letter === '*',
              active: letter === current,
            })}
            onClick={(e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
              e.preventDefault()
              e.stopPropagation()
              scrollView(letter)
              if (letter === '*') {
                selectBrandId && selectBrandId(-1)
              }
              sendTeaCommEvent('clk_event', {
                event_id: 'common_select_car_menu_character',
                button_name: letter,
              })
            }}
          >
            {letter === '*' ? (
              <span
                style={{
                  backgroundImage: `url(${hot_star})`,
                }}
                className={`tw-w-12 tw-h-12 tw-bg-center tw-box-content tw-bg-cover tw-inline-block tw-mt-14 tw-mb-14`}
              />
            ) : (
              letter
            )}
          </li>
        ))}
        <style jsx>
          {`
            ul {
              user-select: none;
              background-color: #f2f4fa;
              height: 100%;
              overflow-x: hidden;
              overflow-y: auto;
              overscroll-behavior: contain;
              cursor: pointer;
            }
            ul::-webkit-scrollbar {
              display: none;
            }
            li {
              border-radius: 2px;
              text-align: center;
              color: #1f2129;
            }
            li:hover {
              background: rgba(255, 225, 0, 0.2);
              cursor: pointer;
            }
            .active {
              color: #1a1a1a;
              background-color: #ffcd32;
            }
            .active:hover {
              background-color: #ffe100;
            }
          `}
        </style>
      </ul>
    )
  }

  function renderBrands() {
    return (
      <div className="brands g-cus-scroll-bar" ref={divRef}>
        {brands.map((brand: IInfoProps[]) => (
          <div key={brand[0].info.pinyin} data-letter={brand[0].info.pinyin}>
            {brand.map((item: IInfoProps) => {
              if (item.type === 1001 || item.type === -1) {
                return (
                  <p
                    key={item.info.brand_name}
                    className={classNames(
                      'brand',
                      `brand-${item.info.brand_id}`,
                      {
                        selected: brandId === item.info.brand_id,
                        'tw-pl-12 tw-sticky tw--top-1': item.type === -1,
                        'tw-pl-4': item.type === 1001,
                      }
                    )}
                    onClick={(e) => {
                      e.stopPropagation()
                      selectBrandId && selectBrandId(item.info.brand_id)
                      selectBrandName && selectBrandName(item.info.brand_name)
                      sendTeaCommEvent('clk_event', {
                        event_id: 'common_select_car_menu_brand',
                        brand_id: `${item.info.brand_id}`,
                        brand_name: item.info.brand_name,
                      })
                    }}
                  >
                    {!!item.info.image_url && (
                      <img
                        src={applyHttpsImage(item.info.image_url || '')}
                        alt=""
                      />
                    )}
                    <span>{item.info.brand_name}</span>
                  </p>
                )
              }
              return (
                <h4
                  className={classNames('letter', {
                    active: item.info.pinyin === current,
                  })}
                  key={item.info.pinyin}
                  data-letter={item.info.pinyin}
                >
                  {item.info.pinyin}
                </h4>
              )
            })}
          </div>
        ))}
        <style jsx>
          {`
            .brands {
              width: 173px;
              height: 100%;
              overflow-x: hidden;
              overflow-y: auto;
              transform: translateZ(0);
              overscroll-behavior: contain;
            }
            .brand {
              height: 40px;
              line-height: 40px;
              cursor: pointer;
              font-size: 14px;
              border-top: 1px solid rgba(216, 216, 216, 0.6);
              border-right: 1px solid rgba(216, 216, 216, 0.6);
              text-align: left;
            }
            .brand.selected {
              background: #f5c530;
              border-right: 1px solid rgba(216, 216, 216, 0.6);
            }
            .brand:hover {
              background: rgba(255, 204, 50, 0.24);
            }
            .brand img {
              width: 22px;
              height: 22px;
              margin-right: 8px;
              border-radius: 50%;
              vertical-align: middle;
              display: inline-block;
            }
            .letter {
              position: sticky;
              top: -1px;
              line-height: 28px;
              height: 28px;
              padding-left: 16px;
              font-size: 12px;
              color: #1f2129;
              padding-left: 12px;
              text-align: left;
              border-top: 1px solid rgba(216, 216, 216, 0.6);
              border-right: 1px solid rgba(216, 216, 216, 0.6);
            }
            .active {
              background-color: #f7f8fc;
            }
          `}
        </style>
      </div>
    )
  }

  return (
    <aside className="clearfix">
      {renderLetters()}
      {renderBrands()}
      <style jsx>
        {`
          aside {
            position: sticky;
            top: 0;
            display: inline-block;
            height: 100%;
            overflow-y: hidden;
            vertical-align: top;
            color: #1f2129;
          }
        `}
      </style>
    </aside>
  )
}
